// src/components/Clinician/RequestDycovueReader.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Stack
} from '@mui/material';
import {
  LocalHospital,
  PriorityHigh,
  Close,
} from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { requestDycovueReport } from '../../services/reporting';

const RequestDycovueReader = ({ open, onClose, study, onSuccess }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const cardBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)';

  const [priority, setPriority] = useState('stat');
  const [clinicalQuestion, setClinicalQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!clinicalQuestion.trim()) {
      setError('Please provide a clinical question or indication');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await requestDycovueReport(study._id, {
        priority,
        clinicalQuestion
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to request Dycovue report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setPriority('stat');
      setClinicalQuestion('');
      setError('');
      onClose();
    }
  };

  const getPriorityInfo = () => {
    switch (priority) {
      case 'stat':
      default:
        return {
          color: 'error',
          icon: <PriorityHigh sx={{ fontSize: 16 }} />,
          description: 'STAT (within 1 hr)',
          helperText: 'Report required within 1 hour'
        };
    }
  };

  const priorityInfo = getPriorityInfo();

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth={false}
      disableEscapeKeyDown={submitting}
      PaperProps={{
        sx: {
          bgcolor: cardBg,
          color: textColorPrimary,
          borderRadius: '12px',
          border: cardBorder,
          width: { xs: '95vw', sm: '720px', md: '760px' },
          maxWidth: '780px !important',
          boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ px: 2, pt: 1.25, pb: 1, borderBottom: cardBorder, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <LocalHospital color="primary" sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '0.95rem' }}>
            Request Dycovue Radiologist Report
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={submitting} sx={{ p: '3px', color: textColorSecondary }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ pt: 0.25 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 1.5, py: 0.5, px: 1.25, fontSize: '0.78rem' }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.75 }}>
            {/* Left Column: Study Info & Priority */}
            <Box>
              {/* Study Information */}
              <Box sx={{ mb: 1.25, p: 1.25, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC', borderRadius: '8px', border: cardBorder }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: textColorSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
                  Study Information
                </Typography>
                <Stack spacing={0.35}>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', color: textColorPrimary }}>
                    <strong>Patient:</strong> {study?.patientName || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', color: textColorPrimary }}>
                    <strong>Study:</strong> {study?.studyDescription || 'No description'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', color: textColorPrimary }}>
                    <strong>Modality:</strong> {study?.modality || '—'} · <strong>Date:</strong> {study?.studyDate ? new Date(study.studyDate).toLocaleDateString() : 'Unknown'}
                  </Typography>
                </Stack>
              </Box>

              {/* Priority Selection */}
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel sx={{ fontSize: '0.8rem', color: textColorSecondary }}>Priority Level *</InputLabel>
                <Select
                  value={priority}
                  label="Priority Level *"
                  onChange={(e) => setPriority(e.target.value)}
                  disabled={submitting}
                  sx={{
                    height: 32,
                    fontSize: '0.8rem',
                    color: textColorPrimary,
                    bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
                    }
                  }}
                >
                  <MenuItem value="stat" sx={{ fontSize: '0.8rem' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PriorityHigh sx={{ fontSize: 16 }} color="error" />
                      <span>STAT (within 1 hr)</span>
                    </Stack>
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Priority Info */}
              <Alert 
                severity={priorityInfo.color === 'error' ? 'error' : priorityInfo.color === 'warning' ? 'warning' : 'info'}
                icon={priorityInfo.icon}
                sx={{ py: 0.25, px: 1.25, fontSize: '0.75rem', '& .MuiAlert-message': { py: 0 } }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem' }}>
                  {priorityInfo.description}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  {priorityInfo.helperText}
                </Typography>
              </Alert>
            </Box>

            {/* Right Column: Clinical Indication & Notice */}
            <Box display="flex" flexDirection="column" justifyContent="space-between">
              {/* Clinical Question */}
              <TextField
                fullWidth
                required
                multiline
                rows={4}
                size="small"
                label="Clinical Question / Indication"
                placeholder="Example: Rule out pulmonary embolism. Patient with sudden onset dyspnea and chest pain."
                value={clinicalQuestion}
                onChange={(e) => setClinicalQuestion(e.target.value)}
                disabled={submitting}
                helperText="Provide clinical context to help the radiologist focus their review"
                FormHelperTextProps={{ sx: { fontSize: '0.7rem', mx: 0.5, mt: 0.25 } }}
                InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
                sx={{
                  mb: 1.25,
                  '& .MuiInputBase-root': {
                    fontSize: '0.8rem',
                    color: textColorPrimary,
                    bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
                  }
                }}
              />

              {/* Information Box */}
              <Alert severity="info" sx={{ py: 0.5, px: 1.25, fontSize: '0.72rem', '& .MuiAlert-message': { py: 0 } }}>
                <Typography variant="body2" sx={{ fontSize: '0.74rem' }} gutterBottom>
                  This will notify available Dycovue radiologists to claim and report on this study.
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', color: textColorSecondary }}>
                  <strong>Note:</strong> A self-report is not required to request a DycoVue report.
                </Typography>
              </Alert>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, borderTop: cardBorder }}>
        <Button 
          onClick={handleClose} 
          disabled={submitting}
          sx={{ height: 30, fontSize: '0.8rem', borderRadius: '6px', textTransform: 'none', px: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !clinicalQuestion.trim()}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <LocalHospital sx={{ fontSize: 16 }} />}
          sx={{
            height: 30,
            fontSize: '0.8rem',
            borderRadius: '6px',
            textTransform: 'none',
            px: 1.75,
            bgcolor: '#0D9488',
            '&:hover': { bgcolor: '#0F766E' }
          }}
        >
          {submitting ? 'Requesting...' : 'Request Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestDycovueReader;